/**
 * useDeploymentTokens Hook
 *
 * React Query hook for fetching deployment tokens for an application.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { deploymentTokenService } from '../../../api/deployment-token.service';
import type {
  DeploymentTokenSanitized,
  DeploymentTokenListQuery,
} from '../../../types/deployment-token.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Hook for fetching deployment tokens list
 */
export const useDeploymentTokens = (
  tenantId: string,
  applicationId: string,
  query?: DeploymentTokenListQuery
): UseQueryResult<DeploymentTokenSanitized[], Error> => {
  return useQuery<DeploymentTokenSanitized[], Error>({
    queryKey: ['deployment-tokens', tenantId, applicationId, query],
    queryFn: async () => {
      console.log('[useDeploymentTokens] Fetching deployment tokens for application:', applicationId);
      const response = await deploymentTokenService.listDeploymentTokens(tenantId, applicationId, query);
      return response.tokens;
    },
    enabled: !!tenantId && !!applicationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      console.error('[useDeploymentTokens] Failed to fetch deployment tokens:', getErrorMessage(error));
    },
  });
};
